/**
 * Chat Service
 * Handles all chat operations via backend API
 *
 * This service provides methods for:
 * - Initializing Stream Chat user and token
 * - Creating direct message and group channels
 * - Deleting and renaming channels
 * - Managing channel members (add/remove)
 */
import { apiClient } from '@/lib/api/client';

export interface InitializeStreamUserResult {
  token: string;
  stream_chat_id: string;
  display_name: string | null;
  image_url: string | null;
}

export interface ChannelResult {
  id: string;
  type: 'messaging' | 'team';
}

export interface SetChannelMemberRoleResult {
  channelId: string;
  targetProfileId: string;
  previousRole: string;
  newRole: string;
  changed: boolean;
}

class ChatService {
  private readonly basePath = '/api/v1/chat';

  /**
   * Initialize Stream Chat user and get connection token
   * Backend extracts user from Bearer JWT token
   * @returns Token and user details for Stream Chat connection
   */
  async initializeStreamUser(): Promise<InitializeStreamUserResult> {
    const response = await apiClient.post<InitializeStreamUserResult>(
      `${this.basePath}/token`
    );

    if (!response) {
      throw new Error('Failed to initialize Stream user');
    }

    return response;
  }

  /**
   * Create a direct message channel between current user and another member
   * Backend uses deterministic IDs to prevent duplicate DM channels
   * @param params - Member profile ID and organization ID
   * @returns Channel ID and type
   */
  async createDirectChannel(params: {
    member_profile_id: string;
    organization_id: string;
  }): Promise<ChannelResult> {
    const response = await apiClient.post<ChannelResult>(
      `${this.basePath}/channels/direct`,
      params
    );

    if (!response) {
      throw new Error('Failed to create direct channel');
    }

    return response;
  }

  /**
   * Create a group channel with multiple members
   * @param params - Group name, member profile IDs, and organization ID
   * @returns Channel ID and type
   */
  async createGroupChannel(params: {
    name: string;
    member_profile_ids: string[];
    organization_id: string;
  }): Promise<ChannelResult> {
    const response = await apiClient.post<ChannelResult>(
      `${this.basePath}/channels/group`,
      params
    );

    if (!response) {
      throw new Error('Failed to create group channel');
    }

    return response;
  }

  /**
   * Delete a channel
   * @param channelId - The channel ID to delete
   * @param channelType - The channel type (e.g., 'messaging', 'team')
   */
  async deleteChannel(channelId: string, channelType: string): Promise<void> {
    await apiClient.delete(`${this.basePath}/channels/${channelId}`, {
      channel_type: channelType,
    });
  }

  /**
   * Rename a channel
   * @param channelId - The channel ID to rename
   * @param channelType - The channel type (e.g., 'messaging', 'team')
   * @param name - The new channel name
   */
  async renameChannel(
    channelId: string,
    channelType: string,
    name: string
  ): Promise<void> {
    await apiClient.patch(`${this.basePath}/channels/${channelId}`, {
      channel_type: channelType,
      name,
    });
  }

  /**
   * Add members to a channel
   * @param params - Channel ID, channel type, and member IDs to add
   */
  async addChannelMembers(params: {
    channelId: string;
    channelType: string;
    memberIds: string[];
  }): Promise<void> {
    await apiClient.post(
      `${this.basePath}/channels/${params.channelId}/members`,
      {
        channel_type: params.channelType,
        member_ids: params.memberIds,
      }
    );
  }

  /**
   * Remove members from a channel
   * Uses query params for DELETE request (apiClient.delete passes params as query params)
   * @param params - Channel ID, channel type, and member IDs to remove
   */
  async removeChannelMembers(params: {
    channelId: string;
    channelType: string;
    memberIds: string[];
  }): Promise<void> {
    await apiClient.delete(
      `${this.basePath}/channels/${params.channelId}/members`,
      {
        channel_type: params.channelType,
        member_ids: params.memberIds,
      }
    );
  }

  /**
   * Promote or demote a single member to/from channel_moderator.
   */
  async setChannelMemberRole(params: {
    channelId: string;
    channelType: string;
    memberId: string;
    action: 'promote' | 'demote';
  }): Promise<SetChannelMemberRoleResult> {
    const response = await apiClient.post<SetChannelMemberRoleResult>(
      `${this.basePath}/channels/${params.channelId}/moderators`,
      {
        channel_type: params.channelType,
        member_id: params.memberId,
        action: params.action,
      }
    );

    if (!response) {
      throw new Error('Failed to update member role');
    }

    return response;
  }
}

// Export singleton instance
export const chatService = new ChatService();
